import { motion } from 'framer-motion';
import { ImageIcon, Brain, Wand2 } from 'lucide-react';
import Image from 'next/image';
import Pbucks from '@/assets/images/P-Bucks.svg';

export const CreditsUsageSection = () => {
  const features = [
    {
      title: 'Generate Images',
      description: 'Create stunning AI-generated images with your P-Bucks',
      icon: <ImageIcon className="h-5 w-5" />,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Train Models',
      description: 'Fine-tune and train your own custom AI models',
      icon: <Brain className="h-5 w-5" />,
      color: 'from-purple-500 to-purple-600',
    },
    {
      title: 'Custom Styles',
      description: 'Apply your unique style with Flux AI and more',
      icon: <Wand2 className="h-5 w-5" />,
      color: 'from-pink-500 to-pink-600',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="relative h-full overflow-hidden rounded-2xl bg-gradient-to-br from-background via-background/95 to-background/90 shadow-2xl"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-1/4 top-0 h-32 w-32 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute -right-1/4 bottom-0 h-32 w-32 rounded-full bg-purple-500/20 blur-3xl" />
      </div>

      <div className="relative flex h-full flex-col p-6">
        <div className="flex flex-col justify-start space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <h2 className="mb-2 flex flex-col gap-1">
              <span className="relative inline-flex items-center">
                <span className="absolute -inset-1 rotate-1 -skew-y-3 rounded-tl-xl rounded-br-xl bg-gradient-to-r from-brand/10 to-purple-500/5 dark:from-brand/20 dark:to-brand/20"></span>
                <span className="relative text-lg font-black tracking-tight">
                  Ready-to-Use
                </span>
              </span>
              <span className="group relative">
                <span className="relative z-10 animate-[gradient-loop_2s_linear_infinite] bg-gradient-to-r from-[#4D4DFF] via-purple-500 to-[#4D4DFF] bg-[length:200%_auto] bg-clip-text text-lg font-extrabold text-transparent">
                  AI Models
                </span>
              </span>
            </h2>
            <p className="text-muted-foreground text-sm">
              Or train your own fine-tuned AI models with state-of-the-art Flux
              AI
            </p>
          </motion.div>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                className="border-border/50 group relative overflow-hidden rounded-lg border bg-background/30 p-3 backdrop-blur-sm transition-all hover:border-primary/50 hover:bg-background/50"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`inline-flex rounded-lg bg-gradient-to-br ${feature.color} p-1.5 text-white shadow-lg`}
                  >
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-xs">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative mt-4 flex-1">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="relative h-full rounded-xl bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 p-4"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.2),rgba(255,255,255,0))]" />
            <div className="bg-grid-white/[0.02] absolute inset-0" />

            <div className="absolute left-1/4 top-1/4 h-16 w-16 rounded-full border border-white/10 bg-gradient-to-br from-blue-500/20 to-purple-500/20 blur-sm" />
            <div className="absolute right-1/4 bottom-1/4 h-20 w-20 rounded-full border border-white/10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 blur-sm" />

            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
                className="relative mx-auto h-20 w-20"
              >
                <Image
                  src={Pbucks}
                  alt="P-Bucks"
                  layout="fill"
                  className="object-contain drop-shadow-lg"
                />
              </motion.div>
            </div>

            <div className="absolute inset-0 bg-gradient-to-t from-transparent via-primary/5 to-transparent opacity-50" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};
